import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Swal from "sweetalert2";

import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchGaragesData } from "../../store/garages/actions";

const Garages = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, loading, error } = useSelector(state => state.Garages);

  useEffect(() => {
    dispatch(fetchGaragesData());
  }, [dispatch]);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `${process.env.REACT_APP_WEB_APP_URL}/api/web/garages/delete/${id}`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (response.status === 200) {
            toast.success('Garage has been deleted.', { autoClose: 3000 });
            dispatch(fetchGaragesData());
          } else {
            toast.error(result?.message || 'Failed to delete', { autoClose: 3000 });
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to delete garage", { autoClose: 3000 });
        }
      }
    });
  };

  const handleRestore = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f8b425",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, restore it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `${process.env.REACT_APP_WEB_APP_URL}/api/web/garages/restore/${id}`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (response.status === 200) {
            toast.success('Garage has been restored.', { autoClose: 3000 });
            dispatch(fetchGaragesData());
          } else {
            toast.error(result?.message || 'Failed to restored', { autoClose: 3000 });
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to restored garage", { autoClose: 3000 });
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: "Image",
        accessorKey: "image",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <img
            src={row.original.image || "assets/images/no-image.png"}
            alt="Image"
            style={{ width: 40, height: 40, borderRadius: "50%" }}
          />
        )
      },
      {
        header: 'Name',
        accessorKey: 'name',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Address',
        accessorKey: 'address',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Is Active',
        accessorKey: 'is_active',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Action',
        id: 'action',
        cell: ({ row }) => {
          const garageId = row.original?.id;
          const is_deleted = row.original?.is_deleted;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success"
                onClick={() => navigate(`/garages/edit/${garageId}`)}
              >
                <i className="mdi mdi-pencil"></i> Edit
              </button>
              {is_deleted == 1 ? (
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => handleRestore(garageId)}
                >
                  <i className="mdi mdi-restore"></i> Restore
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(garageId)}
                >
                  <i className="mdi mdi-delete"></i> Delete
                </button>
              )}
            </div>
          );
        }
      }
    ],
    [navigate]
  );

  const totalRecords = data?.data?.length ?? 0;

  const transformedData = useMemo(() => {
    return data?.data?.map(item => {
      return {
        id: item.id,
        image: item.image || "assets/images/no-image.png",
        name: item.name || "N/A",
        address: item.address || "N/A",
        is_active: item.is_active == 1 ? "Yes" : "No",
        is_deleted: item.is_deleted
      };
    });
  }, [data]);

  document.title = "Garages | Moeen";
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Garages" />
        <div className="float-end d-none d-md-block">
          <Link to="/garages/create"
            color="primary"
            className="btn btn-primary text-white waves-effect waves-light"
          >
            Add Garage
          </Link>
        </div>
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        )}
      </div>
    </div>
  );
};

Garages.propTypes = {
  t: PropTypes.any
};

export default Garages;